import { useEffect, useState, useMemo } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RoomsMap from "@/components/RoomsMap";
import { photoSrc } from "@/components/RoomPhotosUploader";
import FavoriteButton from "@/components/FavoriteButton";
import AddressSearch, { distanceKm } from "@/components/AddressSearch";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Filter } from "lucide-react";

export default function MapPage() {
  const [rooms, setRooms] = useState([]);
  const [slots, setSlots] = useState([]);
  const [region, setRegion] = useState("");
  const [params] = useSearchParams();
  const [city, setCity] = useState(params.get("city") || "");
  const [discipline, setDiscipline] = useState("");
  const [equipment, setEquipment] = useState("");
  const [timeRange, setTimeRange] = useState("");
  const [onlyFree, setOnlyFree] = useState(false);
  const [focus, setFocus] = useState(null);
  const { t } = useI18n();
  const [selected, setSelected] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [showCoaches, setShowCoaches] = useState(true);

  useEffect(() => {
    localStorage.setItem("sc_visited_map", "1");
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    api.get("/coaches").then((r) => setCoaches(r.data.filter((c) => c.lat != null && c.lng != null))).catch(() => {});
    api.get("/slots", { params: { only_available: true } }).then((r) => setSlots(r.data)).catch(() => {});
  }, []);

  const regions = useMemo(() => [...new Set(rooms.map((r) => r.region))].sort(), [rooms]);
  const cities  = useMemo(() => [...new Set(rooms.filter((r) => !region || r.region === region).map((r) => r.city))].sort(), [rooms, region]);
  const disciplines = useMemo(() => [...new Set(rooms.flatMap((r) => r.disciplines || []))].sort(), [rooms]);
  const equipments = useMemo(() => [...new Set(rooms.flatMap((r) => r.equipments || []))].sort(), [rooms]);

  const RANGES = { matin: [6, 12], midi: [12, 14], aprem: [14, 18], soir: [18, 23] };
  const slotInRange = (s) => {
    if (!timeRange) return true;
    const h = new Date(s.start).getHours();
    const [a, b] = RANGES[timeRange];
    return h >= a && h < b;
  };
  const slotsOf = (roomId) => slots.filter((s) => s.room_id === roomId && slotInRange(s));

  const filtered = rooms.filter((r) =>
    (!region || r.region === region) &&
    (!city || r.city === city) &&
    (!discipline || (r.disciplines || []).includes(discipline)) &&
    (!equipment || (r.equipments || []).includes(equipment)) &&
    (!(onlyFree || timeRange) || slotsOf(r.id).length > 0)
  ).map((r) => ({ ...r, distance: focus ? distanceKm(focus, r) : null }))
   .sort((a, b) => (focus ? a.distance - b.distance : 0));
  const activeFilters = [region, city, discipline, equipment, timeRange, onlyFree].filter(Boolean).length;
  const resetFilters = () => { setRegion(""); setCity(""); setDiscipline(""); setEquipment(""); setTimeRange(""); setOnlyFree(false); };

  const filteredCoaches = showCoaches ? coaches.filter((c) => (!city || c.city === city) && (!discipline || (c.specialties || []).includes(discipline))) : [];

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Carte interactive</div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{t("Salles & créneaux disponibles")}</h1>
            <div className="mt-3"><AddressSearch active={!!focus} onLocate={(f) => setFocus({ ...f, zoom: 13 })} onClear={() => setFocus(null)} /></div>
            {focus && <div data-testid="focus-label" className="mt-1 text-xs text-emerald-700 font-semibold">Salles triées par distance depuis : {focus.label}</div>}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Filter className="w-4 h-4 text-slate-500" />
            <select data-testid="filter-region" value={region} onChange={(e) => { setRegion(e.target.value); setCity(""); }} className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm">
              <option value="">Toutes régions</option>
              {regions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="filter-city" value={city} onChange={(e) => setCity(e.target.value)} className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm">
              <option value="">Toutes villes</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="filter-discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value)} className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm">
              <option value="">Toutes disciplines</option>
              {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select data-testid="filter-equipment" value={equipment} onChange={(e) => setEquipment(e.target.value)} className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm">
              <option value="">Tous équipements</option>
              {equipments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select data-testid="filter-time" value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="px-3 py-2 rounded-full border border-slate-200 bg-white text-sm">
              <option value="">Toute la journée</option>
              <option value="matin">Matin (6h–12h)</option>
              <option value="midi">Midi (12h–14h)</option>
              <option value="aprem">Après-midi (14h–18h)</option>
              <option value="soir">Soir (18h–23h)</option>
            </select>
            <label data-testid="toggle-only-free" className={`flex items-center gap-2 px-3 py-2 rounded-full border text-sm cursor-pointer select-none ${onlyFree ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-white border-slate-200 text-slate-600"}`}>
              <input type="checkbox" className="accent-emerald-500" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} />
              Créneaux libres
            </label>
            {activeFilters > 0 && <button data-testid="reset-filters" onClick={resetFilters} className="text-xs text-slate-500 underline px-2">Réinitialiser ({activeFilters})</button>}
            <label data-testid="toggle-coaches" className={`flex items-center gap-2 px-3 py-2 rounded-full border text-sm cursor-pointer select-none ${showCoaches ? "bg-orange-500/10 border-orange-500/30 text-orange-700" : "bg-white border-slate-200 text-slate-600"}`}>
              <input type="checkbox" className="accent-orange-500" checked={showCoaches} onChange={(e) => setShowCoaches(e.target.checked)} />
              Coachs ({coaches.length})
            </label>
          </div>
        </div>

        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 h-[520px] lg:h-[calc(100vh-240px)] rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <RoomsMap rooms={filtered} coaches={filteredCoaches} onSelect={setSelected} focus={focus} />
          </div>
          <div className="lg:col-span-5 space-y-3 max-h-[calc(100vh-240px)] overflow-y-auto pr-1 sc-fade-in">
            <div className="text-sm text-slate-600 flex items-center gap-3">
              <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1" />{filtered.length} salle(s)</span>
              <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-500 mr-1" />{filteredCoaches.length} coach(s) sur la carte</span>
            </div>
            {filteredCoaches.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="map-coach-strip">
                {filteredCoaches.map((c) => (
                  <Link key={c.id} to={`/coachs/${c.id}`} data-testid={`map-coach-${c.id}`} className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-orange-500/30 text-xs hover:bg-orange-50 transition-colors">
                    <span className="w-6 h-6 rounded-full bg-orange-500/10 text-orange-700 grid place-items-center font-bold text-[10px]">{(c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</span>
                    <span className="font-semibold text-slate-800">{c.name}</span>
                    <span className="text-slate-500">{c.city}</span>
                  </Link>
                ))}
              </div>
            )}
            {filtered.map((r) => {
              const slotsOfRoom = slotsOf(r.id);
              return (
                <div key={r.id} data-testid={`room-card-${r.id}`} className={`sc-card cursor-pointer ${selected?.id === r.id ? "ring-2 ring-emerald-500" : ""}`} onClick={() => setSelected(r)}>
                  <div className="flex gap-3">
                    {r.photo_url && <img src={photoSrc(r.photo_url)} alt={r.name} className="w-20 h-20 object-cover rounded-xl" />}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-slate-900">{r.name}</div>
                        <div className="text-sm font-semibold text-emerald-700">{r.price_per_hour}€/h</div>
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{r.neighborhood} · {r.city}{r.distance != null && <span data-testid={`room-distance-${r.id}`} className="ml-1 font-semibold text-emerald-700">· à {r.distance < 1 ? `${Math.round(r.distance * 1000)} m` : `${r.distance.toFixed(1)} km`}</span>}</div>
                      <div className="mt-1 text-xs text-slate-600">{(r.disciplines || []).slice(0, 3).join(" · ")}</div>
                      <div className="mt-2 text-xs text-slate-500">{slotsOfRoom.length} créneau(x) libres{timeRange ? " sur cette tranche" : ""}</div>
                      {(r.equipments || []).length > 0 && <div className="mt-1 text-[11px] text-slate-500">{r.equipments.slice(0, 4).join(" · ")}</div>}
                    </div>
                  </div>
                  <div className="mt-3 flex justify-between items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-700 border border-indigo-500/20">
                      Coopération : {r.cooperation_level === "open" ? "Ouverte" : r.cooperation_level === "moderate" ? "Modérée" : "Stricte"}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <FavoriteButton roomId={r.id} compact />
                      <Link data-testid={`room-view-${r.id}`} to={`/salles/${r.id}`} className="sc-btn sc-btn-outline text-xs">Voir les créneaux</Link>
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="text-slate-500 text-sm p-4">Aucun résultat avec ces filtres. <button data-testid="reset-filters-empty" onClick={resetFilters} className="underline">Réinitialiser</button></div>}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
